/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { ArrowRight, UserCheck } from 'lucide-react';
import { shiftReplacementAPI } from '../../lib/api';

/**
 * ReplacementWeekCard — pure operational visibility, not analytics.
 *
 *   This week
 *   • 4 cover requests
 *   • 3 accepted · 1 expired
 *   • 75% success rate
 *
 * Reads `/api/shift-replacements/stats` (server-cached 60s). No client
 * refetch loop — single fetch per dashboard mount.
 */
export function ReplacementWeekCard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await shiftReplacementAPI.stats();
      setStats(r.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!stats) return null;
  // Hide the card entirely on weeks with zero activity — keeps dashboard clean
  if (stats.total === 0) return null;

  const decided = stats.accepted + stats.declined + stats.expired;
  const successBand =
    decided === 0 ? 'slate'
      : stats.success_rate >= 75 ? 'emerald'
      : stats.success_rate >= 50 ? 'amber' : 'rose';
  const bandCls = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    slate: 'text-slate-500',
  }[successBand];

  return (
    <Card
      className="border-slate-200 cursor-pointer hover:shadow-sm transition-shadow"
      onClick={() => navigate('/shift-replacements')}
      data-testid="replacement-week-card"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
            <UserCheck className="text-indigo-600" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">This week — replacements</p>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums">{stats.total}</span>
              <span className="text-xs text-slate-500">cover requests</span>
            </div>
            <div className="mt-1 text-xs text-slate-600 flex flex-wrap gap-x-2 gap-y-0.5">
              <span className="tabular-nums"><b>{stats.accepted}</b> accepted</span>
              {stats.declined > 0 && <span className="tabular-nums"><b>{stats.declined}</b> declined</span>}
              {stats.expired > 0 && <span className="tabular-nums"><b>{stats.expired}</b> expired</span>}
              {stats.pending > 0 && <span className="tabular-nums text-amber-700"><b>{stats.pending}</b> pending</span>}
            </div>
            {decided > 0 && (
              <p className={`mt-1.5 text-xs font-medium ${bandCls}`} data-testid="replacement-success-rate">
                {stats.success_rate}% success rate
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ReplacementWeekCard;
